/* eslint-disable require-unicode-regexp  */

import type { SyncBlockSourceInfo } from '../../providers/types';
import { fetchWithRetry } from '../../utils/retry';

import { getJiraIssueAriFromSourceAri } from './ari';

const COMMON_HEADERS = {
	'Content-Type': 'application/json',
	Accept: 'application/json',
};

const AGG_HEADERS = {
	'X-ExperimentalApi': 'confluence-agg-beta',
};

const GRAPHQL_ENDPOINT = '/gateway/api/graphql';

const GET_SOURCE_INFO_OPERATION_NAME = 'EDITOR_SYNCED_BLOCK_GET_SOURCE_INFO';

type GetSourceInfoResult = {
	data: {
		jira: {
			issueById: {
				id: string;
				/**
				 * Issue-type metadata used by the SyncedLocationDropdown to render the correct
				 * ADS icon. Optional in the AGG schema, may be `null` for partially indexed
				 * issues.
				 */
				issueType?: {
					avatar?: {
						xsmall: string | null;
					} | null;
					name: string | null;
				} | null;
				// `summary` and `webUrl` are nullable in the AGG schema for partially indexed
				// issues; surface that here so callers must defensively narrow before use.
				summary: string | null;
				webUrl: string | null;
			} | null;
		} | null;
	};
};

/**
 * Query to get the work item url by id
 * @param id - the ID of the work item
 * @returns url of the work item
 */
// `issueType` is requested alongside `summary` and `webUrl` so the
// SyncedLocationDropdown can render the correct ADS issue-type icon.
const GET_SOURCE_INFO_QUERY = `query ${GET_SOURCE_INFO_OPERATION_NAME} ($id: ID!) {
  jira {
	issueById(id: $id) {
	id
	webUrl
	summary
	issueType {
		name
		avatar {
		xsmall
		}
	}
	}
  }}`;

const getJiraWorkItemSourceInfo = async (issueAri: string): Promise<GetSourceInfoResult> => {
	const bodyData = {
		query: GET_SOURCE_INFO_QUERY,
		operationName: GET_SOURCE_INFO_OPERATION_NAME,
		variables: {
			id: issueAri,
		},
	};

	const response = await fetchWithRetry(GRAPHQL_ENDPOINT, {
		method: 'POST',
		headers: { ...COMMON_HEADERS, ...AGG_HEADERS },
		body: JSON.stringify(bodyData),
	});

	if (!response.ok) {
		throw new Error(`Failed to get url: ${response.statusText}`);
	}

	return (await response.json()) as GetSourceInfoResult;
};

const resolveNoAccessWorkItemInfo = async (
	sourceAri: string,
	issueAri: string,
): Promise<SyncBlockSourceInfo> => {
	const response = await fetch('/gateway/api/object-resolver/resolve/ari', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: 'application/json',
		},
		body: JSON.stringify({ ari: issueAri }),
	});

	if (response.ok) {
		const payload = await response.json();
		const url = payload?.data?.url;
		const title = payload?.data?.name;

		return {
			url: typeof url === 'string' ? url : undefined,
			title: typeof title === 'string' ? title : undefined,
			sourceAri,
		};
	} else {
		throw new Error(`Failed to resolve ari: ${response.statusText}`);
	}
};

/**
 * Fetch source-info metadata for a Jira work item used by reference sync blocks.
 *
 * Parity notes vs the Confluence equivalent (`fetchConfluencePageInfo`):
 *
 * - **Archived / draft URL variants** are intentionally omitted. Confluence pages can be
 *   "unpublished" (draft, archived, in-trash) and need REST fallbacks plus alternate URL
 *   shapes. Jira work items have no equivalent lifecycle — an issue either exists in AGG
 *   or it does not — so a single `webUrl` is sufficient.
 *
 * - **`#block-{localId}` deep-link anchor** is intentionally not appended. The current
 *   Jira issue view does not implement scroll-to-anchor for unknown fragments, so the
 *   anchor would be dead weight on the URL. The dispatching code in
 *   `syncBlockProvider.fetchSyncBlockSourceInfo` deliberately does not pass `localId`
 *   here for the same reason. If/when Jira issue view supports anchor scrolling for
 *   sync-block local IDs, accept `localId?: string` here and append `#block-{localId}`.
 *
 * - **`subType` / page-type variants** are not modeled — Jira issue type is exposed via
 *   the separate `issueType` field returned alongside `summary` / `webUrl`.
 */
export const fetchJiraWorkItemInfo = async (
	workItemAri: string,
	hasAccess: boolean,
): Promise<SyncBlockSourceInfo | undefined> => {
	// AGG `issueById` and object-resolver only understand issue ARIs. Field-value
	// source ARIs keep their original identity on the returned `sourceAri`.
	const issueAri = getJiraIssueAriFromSourceAri({ ari: workItemAri });

	if (hasAccess) {
		const response = await getJiraWorkItemSourceInfo(issueAri);

		const contentData = response.data?.jira?.issueById;

		const webUrl = typeof contentData?.webUrl === 'string' ? contentData.webUrl : undefined;
		const summary = typeof contentData?.summary === 'string' ? contentData.summary : undefined;

		// Surface issue-type metadata for the SyncedLocationDropdown's ADS icon.
		// Defensive narrowing: only surface when `name` is a non-empty string;
		// AGG values like `{ name: null }` collapse back to `undefined`.
		const issueTypeName = contentData?.issueType?.name;
		const issueType =
			typeof issueTypeName === 'string' && issueTypeName.length > 0
				? {
						name: issueTypeName,
						iconUrl:
							typeof contentData?.issueType?.avatar?.xsmall === 'string'
								? contentData.issueType.avatar.xsmall
								: undefined,
					}
				: undefined;

		return Promise.resolve({
			url: webUrl,
			sourceAri: workItemAri,
			title: summary,
			issueType,
		});
	} else {
		return await resolveNoAccessWorkItemInfo(workItemAri, issueAri);
	}
};
